/**
 * cart-interceptor.js — 仅用 classic script（非模块），由 layout/theme.liquid 在
 * {{ content_for_header }} 之前同步加载，抢在钱包 / Shop Pay 等脚本之前 patch fetch。
 *
 * 数据来源（须先于本文件）：snippets/cart-interceptor-config.liquid 写入
 * window.__ROBOSEN_GIFT_MASTER_IDS__、window.__ROBOSEN_FBT_CART_ADD_PAIRS__。
 *
 * window.fetch：
 * - getter 固定返回 patchedFetch，setter 收到外部赋值时写入 _nativeFetch；
 * - configurable: false，避免其它脚本删掉整条 accessor。
 * - 实际转发与改写后的请求一律走 _trueFetch（补丁瞬间捕获的原生 fetch），不调用 _nativeFetch，
 *   否则常见写法 `(…args) => window.fetch(...)` 会再次进入 patchedFetch，栈溢出。
 * - _nativeFetch 仅供调试观测「谁又包了一层」，运行时逻辑不读取。
 *
 * 拦截范围：仅当第二个参数 init.body 为 **字符串** 且可 JSON 解析时，才尝试改 GraphQL cartCreate
 * 或 REST POST /cart/add.js；FormData、未读 body 的 Request 等一律原样 forward。
 */

(function cartInterceptorEarlyFetchPatch() {
  const masterIds = window.__ROBOSEN_GIFT_MASTER_IDS__;
  /** 与 gift-config 中 FBT 加购配对一致；config 缺失时用内联兜底 */
  const FBT_CART_ADD_PAIRS = Array.isArray(window.__ROBOSEN_FBT_CART_ADD_PAIRS__)
    ? window.__ROBOSEN_FBT_CART_ADD_PAIRS__
    : [
        [46987341299925, 46871888560341],
        [51280792289493, 51275836457173],
      ];

  /** cartCreate lines[].merchandiseId 若落在此集合，才允许按规则加赠品行 */
  const GIFT_ELIGIBLE_FIRST_LINE_GQL = new Set(
    (Array.isArray(masterIds) ? masterIds : []).map(function (id) {
      return "gid://shopify/ProductVariant/" + String(id);
    }),
  );

  if (GIFT_ELIGIBLE_FIRST_LINE_GQL.size === 0) {
    console.warn("[cart-interceptor] __ROBOSEN_GIFT_MASTER_IDS__ missing or empty — cartCreate injection disabled");
    return;
  }

  function variantGid(id) {
    return "gid://shopify/ProductVariant/" + id;
  }

  /** 宿主原生 fetch，闭包内永不改换 */
  const _trueFetch = window.fetch;
  /** setter 收到的「别人包一层的 fetch」；本文件不用于发请求 */
  let _nativeFetch = _trueFetch;

  /** 归一化 fetch 第一个参数上的 URL（string / URL / Request） */
  function getFetchUrlString(url) {
    if (typeof url === "string") return url;
    if (url instanceof URL) return url.href;
    if (url && typeof url === "object" && "url" in url) return String(url.url);
    return String(url);
  }

  function isCartAddJsPath(href) {
    try {
      return new URL(href, location.origin).pathname.endsWith("/cart/add.js");
    } catch {
      return href.includes("/cart/add.js");
    }
  }

  /** 礼品区 DOM 当前选中的 variant id；礼品块尚未渲染时为 null */
  function getSelectedGiftVariantId() {
    const list = document.querySelector(".product-page-gift .product-list");
    if (!list) return null;

    var item = list.querySelector(".product-item.selected[data-product-id]");
    if (item && item.dataset.productId) {
      var n1 = Number(item.dataset.productId);
      return Number.isNaN(n1) ? null : n1;
    }

    var withId = list.querySelector("[data-product-id].selected");
    if (withId && withId.dataset.productId) {
      var n2 = Number(withId.dataset.productId);
      return Number.isNaN(n2) ? null : n2;
    }

    return null;
  }

  /** /cart/add.js JSON items 里的数字 variant id */
  function itemVariantIdSet(items) {
    return new Set(
      items
        .map(function (i) {
          return i && i.id != null ? Number(i.id) : null;
        })
        .filter(function (n) {
          return n != null && !Number.isNaN(n);
        }),
    );
  }

  /** 仅当购物车行里同时出现某对 FBT variant 时，REST 分支才追加赠品 */
  function fbtPairMatchesForGiftAdd(idSet) {
    return FBT_CART_ADD_PAIRS.some(function (pair) {
      var a = pair[0];
      var b = pair[1];
      return idSet.has(a) && idSet.has(b);
    });
  }

  /** 用 JSON.stringify 重写 body 后必须仍走 _trueFetch，绕过 accessor */
  function fetchJsonBody(url, init, data) {
    return _trueFetch.call(window, url, Object.assign({}, init, { body: JSON.stringify(data) }));
  }

  /**
   * 检查购物车商品行中是否包含符合赠品条件的主商品
   * @param {Array} lines - 购物车商品行数组，每个元素需包含 merchandiseId 字段
   * @returns {boolean} 若存在符合条件的主商品则返回true，否则返回false
   */
  function linesHaveEligibleMaster(lines) {
    if (!Array.isArray(lines)) return false;
    return lines.some(function (line) {
      var mid = line && line.merchandiseId;
      return mid && GIFT_ELIGIBLE_FIRST_LINE_GQL.has(mid);
    });
  }

  /**
   * 检查购物车商品行中是否已包含指定GID的商品
   * @param {Array} lines - 购物车商品行数组，每个元素需包含 merchandiseId 字段
   * @param {string} merchandiseGid - 要检查的商品GID（格式如"gid://shopify/ProductVariant/123"）
   * @returns {boolean} 若已包含指定GID的商品则返回true，否则返回false
   */
  function linesAlreadyHaveMerchandise(lines, merchandiseGid) {
      if (!Array.isArray(lines) || !merchandiseGid) return false;
      return lines.some(function (line) {
        return line && line.merchandiseId === merchandiseGid;
      });
    }

  function patchedFetch() {
    var args = arguments;
    var url = args[0];
    var init = args[1];
    var o = init != null ? init : {};
    var href = getFetchUrlString(url);
    var bodyStr = o.body;

    var forward = function () {
      return _trueFetch.apply(window, args);
    };

    // GraphQL cartCreate（如 Shop Pay）：满足主品规则则追加一行赠品 merchandiseId
    if (href.includes("graphql.json") && typeof bodyStr === "string" && bodyStr.includes("cartCreate")) {
      var data;
      try {
        data = JSON.parse(bodyStr);
      } catch {
        return forward();
      }

      var lines = data && data.variables && data.variables.input && data.variables.input.lines;
      var addId = getSelectedGiftVariantId();
      var giftGid = addId != null ? variantGid(addId) : null;
      var hasEligible = linesHaveEligibleMaster(lines);
      var alreadyGift = linesAlreadyHaveMerchandise(lines, giftGid);

      var input = data && data.variables && data.variables.input;
      // 如果 addId 存在，且有 主商品 在配置文件中，且商品 gift 存在，且未包含指定 gift 的商品，且input存在，则添加赠品
      if (addId && hasEligible && giftGid && !alreadyGift && input) {
        if (!input.lines) input.lines = [];
        input.lines.push({
          quantity: 1,
          merchandiseId: giftGid,
        });
      }
      return fetchJsonBody(url, o, data);
    }

    // REST：仅 JSON POST /cart/add.js + items 数组 + FBT 命中时多塞一条赠品
    if (isCartAddJsPath(href) && String(o.method || "").toUpperCase() === "POST" && typeof bodyStr === "string") {
      var dataRest;
      try {
        dataRest = JSON.parse(bodyStr);
      } catch {
        return forward();
      }
      var items = dataRest && dataRest.items;
      if (!Array.isArray(items) || !items.length) {
        return forward();
      }
      var ids = itemVariantIdSet(items);
      if (!fbtPairMatchesForGiftAdd(ids)) {
        return forward();
      }
      var giftId = getSelectedGiftVariantId();
      if (giftId == null || ids.has(giftId)) {
        return forward();
      }
      return fetchJsonBody(url, o, Object.assign({}, dataRest, {
        items: items.concat([{ id: giftId, quantity: 1 }]),
      }));
    }

    return forward();
  }

  try {
    Object.defineProperty(window, "fetch", {
      get: function () {
        return patchedFetch;
      },
      set: function (incoming) {
        if (incoming === patchedFetch) return;
        if (typeof incoming === "function") {
          _nativeFetch = incoming;
        }
      },
      configurable: false,
      enumerable: true,
    });
  } catch (defineErr) {
    try {
      window.fetch = patchedFetch;
      console.warn("[cart-interceptor] defineProperty failed, fallback assign", defineErr);
    } catch (e) {
      console.warn("[cart-interceptor] fetch patch failed", e);
    }
  }
})();
